import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import { getQuotaAddingRepInfo, getQuotaAddingQuarters, createQuotaInfo } from '../redux/management.thunk';
import { selectIsCreateSuccess, selectQuotaAddingQuarters, selectTeamHierarchy, setTempHireDate, setTempTeam, clearQuotaAddingRepInfo } from '../redux/management.slice';
import RepInfoSection from './RepInfoSection';
import QuotaAssignmentSection from './QuotaAssignmentSection';

const useStyles = makeStyles((theme) => ({
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalPaper: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: '8px',
    boxShadow: theme.shadows[5],
    padding: theme.spacing(4),
    width: '950px',
    maxHeight: '90vh',
    overflow: 'auto',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: theme.spacing(3),
  },
  closeButton: {
    marginTop: -theme.spacing(1),
    marginRight: -theme.spacing(1),
  },
  section: {
    marginBottom: theme.spacing(3),
  },
  actions: {
    display: 'flex',
    justifyContent: 'center',
    gap: theme.spacing(2),
    marginTop: theme.spacing(3),
  },
  dialogActions: {
    justifyContent: 'center',
    paddingBottom: theme.spacing(2),
  }
}));

const QuotaEditorModal = ({ open, onClose, selectedUser, selectedUserId }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const currencySymbol = useSelector(state => state.app.user.Currency);
  const { quotaAddingRepInfo } = useSelector((state) => state.management);
  const quotaAddingQuarters = useSelector(selectQuotaAddingQuarters);
  const teamHierarchy = useSelector(selectTeamHierarchy);

  const [quotaAssignments, setQuotaAssignments] = useState([]);
  const [initialQuotaAssignments, setInitialQuotaAssignments] = useState([]);
  const [hireDate, setHireDate] = useState('');
  const [initialHireDate, setInitialHireDate] = useState('');
  const [team, setTeam] = useState('');
  const [initialTeam, setInitialTeam] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'error' });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isLoadingRepInfo, setIsLoadingRepInfo] = useState(false);

  const allAvailableQuartersSorted = useMemo(() => {
    if (!quotaAddingQuarters) return [];
    const quarters = [
      ...(quotaAddingQuarters?.previousQuarters || []),
      quotaAddingQuarters?.currentQuarter,
      ...(quotaAddingQuarters?.futureQuarters || [])
    ].filter(Boolean);
    const uniqueQuarters = [...new Set(quarters)];
    return uniqueQuarters.sort((a, b) => {
      const [yearA, quarterA] = a.split('-Q').map(Number);
      const [yearB, quarterB] = b.split('-Q').map(Number);
      if (yearA !== yearB) return yearA - yearB;
      return quarterA - quarterB;
    });
  }, [quotaAddingQuarters]);

  useEffect(() => {
    if (open) {
      setIsLoadingRepInfo(true);
      dispatch(getQuotaAddingRepInfo({ userId: selectedUserId }));
      if (!quotaAddingQuarters) {
        dispatch(getQuotaAddingQuarters({ type: 'rep' }));
      }
    }
  }, [open, selectedUserId, dispatch, quotaAddingQuarters]);

  // Set loading to false when quotaAddingRepInfo is loaded
  useEffect(() => {
    if (quotaAddingRepInfo) {
      setIsLoadingRepInfo(false);
    }
  }, [quotaAddingRepInfo]);

  useEffect(() => {
    if (quotaAddingRepInfo?.Hire_date) {
      setHireDate(quotaAddingRepInfo.Hire_date);
      setInitialHireDate(quotaAddingRepInfo.Hire_date);
    }
    if (quotaAddingRepInfo?.Display_Name) {
      const teamValue = Array.isArray(quotaAddingRepInfo.Display_Name)
        ? quotaAddingRepInfo.Display_Name[0]
        : quotaAddingRepInfo.Display_Name;
      setTeam(teamValue);
      setInitialTeam(teamValue);
    }
  }, [quotaAddingRepInfo]);

  // Track if we've initialized the assignments for this modal opening
  const [hasInitialized, setHasInitialized] = useState(false);

  // Reset initialization flag when modal closes
  useEffect(() => {
    if (!open) {
      setHasInitialized(false);
    }
  }, [open]);

  useEffect(() => {
    if (open && quotaAddingQuarters && allAvailableQuartersSorted.length > 0 && !hasInitialized) {
      // Check if availableQuota exists and has data for pre-filling
      if (quotaAddingRepInfo?.availableQuota && Array.isArray(quotaAddingRepInfo.availableQuota) && quotaAddingRepInfo.availableQuota.length > 0) {
        console.log('Pre-filling with availableQuota:', quotaAddingRepInfo.availableQuota);
        // Pre-fill with existing quota data
        const preFilledQuarters = quotaAddingRepInfo.availableQuota.map((quotaData, index) => ({
          id: Date.now() + Math.random() + index,
          fiscalQuarter: quotaData.Fiscal_Quarter,
          quota: quotaData.Quota ? quotaData.Quota.toString() : '',
          nqpGoal: quotaData.NQP_GOAL ? quotaData.NQP_GOAL.toString() : '',
          rampStatus: quotaData.Ramp_Status || 'Ramped',
        }));
        console.log('Setting preFilledQuarters:', preFilledQuarters);
        setQuotaAssignments(preFilledQuarters);
        setInitialQuotaAssignments(JSON.parse(JSON.stringify(preFilledQuarters)));
        setHasInitialized(true);
      } else if (quotaAddingRepInfo && (!quotaAddingRepInfo.availableQuota || (Array.isArray(quotaAddingRepInfo.availableQuota) && quotaAddingRepInfo.availableQuota.length === 0))) {
        // Initialize with default quarters if quotaAddingRepInfo has loaded and there's no availableQuota or it's an empty array
        console.log('No availableQuota or empty array, using default quarters');
        const { currentQuarter } = quotaAddingQuarters;
        const currentIndex = allAvailableQuartersSorted.indexOf(currentQuarter);
        if (currentIndex >= 0) {
          const initialQuarters = allAvailableQuartersSorted.slice(currentIndex, currentIndex + 3);
          const defaultQuarters = initialQuarters.map((quarter, index) => ({
            id: Date.now() + Math.random() + index,
            fiscalQuarter: quarter,
            quota: '',
            nqpGoal: '',
            rampStatus: 'Ramped',
          }));
          setQuotaAssignments(defaultQuarters);
          setInitialQuotaAssignments(JSON.parse(JSON.stringify(defaultQuarters)));
          setHasInitialized(true);
        }
      }
    }
  }, [open, quotaAddingQuarters, allAvailableQuartersSorted, hasInitialized, quotaAddingRepInfo]);

  const handleClose = useCallback(() => {
    setQuotaAssignments([]);
    setHireDate('');
    setTeam('');
    setIsSaving(false);
    setConfirmOpen(false);
    dispatch(clearQuotaAddingRepInfo());
    onClose();
  }, [onClose, dispatch]);

  const handleTeamChange = (val) => {
    if (Array.isArray(val)) {
      setTeam(val[0] || '');
    } else {
      setTeam(val || '');
    }
  };

  const canAddPreviousQuarter = useMemo(() => {
    if (quotaAssignments.length === 0 || allAvailableQuartersSorted.length === 0) return false;
    const firstQuarter = quotaAssignments[0].fiscalQuarter;
    const firstAvailableQuarter = allAvailableQuartersSorted[0];
    return firstQuarter !== firstAvailableQuarter;
  }, [quotaAssignments, allAvailableQuartersSorted]);

  const canAddNextQuarter = useMemo(() => {
    if (allAvailableQuartersSorted.length === 0) return false;
    if (quotaAssignments.length === 0) return true;
    const lastQuarter = quotaAssignments[quotaAssignments.length - 1].fiscalQuarter;
    const lastAvailableQuarter = allAvailableQuartersSorted[allAvailableQuartersSorted.length - 1];
    return lastQuarter !== lastAvailableQuarter;
  }, [quotaAssignments, allAvailableQuartersSorted]);

  const handleAddPreviousQuarter = () => {
    if (quotaAssignments.length === 0 || allAvailableQuartersSorted.length === 0) return;
    const firstQuarter = quotaAssignments[0].fiscalQuarter;
    const currentIndex = allAvailableQuartersSorted.indexOf(firstQuarter);
    if (currentIndex > 0) {
      const previousQuarter = allAvailableQuartersSorted[currentIndex - 1];

      // Check if this quarter exists in initial data to restore values
      const initialAssignment = initialQuotaAssignments.find(
        assignment => assignment.fiscalQuarter === previousQuarter
      );

      const newAssignment = {
        id: Date.now() + Math.random(),
        fiscalQuarter: previousQuarter,
        quota: initialAssignment ? initialAssignment.quota : '',
        nqpGoal: initialAssignment ? initialAssignment.nqpGoal : '',
        rampStatus: initialAssignment ? initialAssignment.rampStatus : 'Ramped',
      };
      setQuotaAssignments([newAssignment, ...quotaAssignments]);
    }
  };

  const handleAddNextQuarter = () => {
    if (allAvailableQuartersSorted.length === 0) return;
    if (quotaAssignments.length === 0) {
      if (quotaAddingQuarters?.currentQuarter) {
        const currentQuarter = quotaAddingQuarters.currentQuarter;

        // Check if this quarter exists in initial data to restore values
        const initialAssignment = initialQuotaAssignments.find(
          assignment => assignment.fiscalQuarter === currentQuarter
        );

        const newAssignment = {
          id: Date.now() + Math.random(),
          fiscalQuarter: currentQuarter,
          quota: initialAssignment ? initialAssignment.quota : '',
          nqpGoal: initialAssignment ? initialAssignment.nqpGoal : '',
          rampStatus: initialAssignment ? initialAssignment.rampStatus : 'Ramped',
        };
        setQuotaAssignments([newAssignment]);
      }
      return;
    }
    const lastQuarter = quotaAssignments[quotaAssignments.length - 1].fiscalQuarter;
    const currentIndex = allAvailableQuartersSorted.indexOf(lastQuarter);
    if (currentIndex >= 0 && currentIndex < allAvailableQuartersSorted.length - 1) {
      const nextQuarter = allAvailableQuartersSorted[currentIndex + 1];

      // Check if this quarter exists in initial data to restore values
      const initialAssignment = initialQuotaAssignments.find(
        assignment => assignment.fiscalQuarter === nextQuarter
      );

      const newAssignment = {
        id: Date.now() + Math.random(),
        fiscalQuarter: nextQuarter,
        quota: initialAssignment ? initialAssignment.quota : '',
        nqpGoal: initialAssignment ? initialAssignment.nqpGoal : '',
        rampStatus: initialAssignment ? initialAssignment.rampStatus : 'Ramped',
      };
      setQuotaAssignments([...quotaAssignments, newAssignment]);
    }
  };

  const handleQuotaChange = (id, field, value) => {
    setQuotaAssignments(prev =>
      prev.map(a => (a.id === id ? { ...a, [field]: value } : a))
    );
  };

  const handleRemoveQuota = (id) => {
    setQuotaAssignments(prev => prev.filter(a => a.id !== id));
  };

  const isValidRecord = (assignment) => {
    const hasQuota = assignment.quota && String(assignment.quota).trim() !== '';
    const hasNqpGoal = assignment.nqpGoal && String(assignment.nqpGoal).trim() !== '';
    return hasQuota || hasNqpGoal;
  };

  const hasAtLeastOneValidAssignment = useMemo(() => {
    return quotaAssignments.some(isValidRecord);
  }, [quotaAssignments]);

  // Function to compare two quota assignment arrays (ignoring IDs)
  const quotaAssignmentsChanged = useMemo(() => {
    if (quotaAssignments.length !== initialQuotaAssignments.length) {
      return true;
    }

    // Sort both arrays by fiscalQuarter for comparison
    const sortByQuarter = (a, b) => {
      const [yearA, quarterA] = a.fiscalQuarter.split('-Q').map(Number);
      const [yearB, quarterB] = b.fiscalQuarter.split('-Q').map(Number);
      if (yearA !== yearB) return yearA - yearB;
      return quarterA - quarterB;
    };

    const currentSorted = [...quotaAssignments].sort(sortByQuarter);
    const initialSorted = [...initialQuotaAssignments].sort(sortByQuarter);

    for (let i = 0; i < currentSorted.length; i++) {
      const current = currentSorted[i];
      const initial = initialSorted[i];

      if (
        current.fiscalQuarter !== initial.fiscalQuarter ||
        current.quota !== initial.quota ||
        current.nqpGoal !== initial.nqpGoal ||
        current.rampStatus !== initial.rampStatus
      ) {
        return true;
      }
    }

    return false;
  }, [quotaAssignments, initialQuotaAssignments]);

  const hasChanges = useMemo(() => {
    const hireDateChanged = hireDate !== initialHireDate;
    const teamChanged = team !== initialTeam;

    return hireDateChanged || teamChanged || quotaAssignmentsChanged;
  }, [hireDate, initialHireDate, team, initialTeam, quotaAssignmentsChanged]);

  const handleSave = () => {
    const errorMessages = [];
    if (!team || team.length === 0) {
      errorMessages.push('Please select a team.');
    }
    if (!hireDate) {
      errorMessages.push('Please select a hire date.');
    }

    if (errorMessages.length > 0) {
      setAlert({ open: true, message: errorMessages.join(' '), severity: 'error' });
      return;
    }
    setAlert({ open: false, message: '', severity: 'error' });
    setConfirmOpen(true);
  };
  const isCreateSuccess = useSelector(selectIsCreateSuccess)
  useEffect(()=>{
    if(isCreateSuccess) {
      handleClose()
    }
  },[isCreateSuccess, handleClose])

  const handleConfirmSave = () => {
    setConfirmOpen(false);
    setIsSaving(true);

    // Dispatch temp hireDate and team to Redux store
    dispatch(setTempHireDate(hireDate));
    dispatch(setTempTeam(team)); // send string

    const validAssignments = quotaAssignments.filter(isValidRecord);
    setQuotaAssignments(validAssignments);

    // Check if team or hire date has changed
    const hireDateChanged = hireDate !== initialHireDate;
    const teamChanged = team !== initialTeam;

    // If team or hire date changed, we need to send all assignments
    // Otherwise, only send modified assignments
    let assignmentsToSend;
    if (hireDateChanged || teamChanged) {
      // Send all valid assignments when team or hire date changes
      assignmentsToSend = validAssignments;
    } else {
      // Only include assignments that have been modified
      assignmentsToSend = validAssignments.filter(assignment => {
        // Find the corresponding initial assignment
        const initialAssignment = initialQuotaAssignments.find(
          initial => initial.fiscalQuarter === assignment.fiscalQuarter
        );

        // If no initial assignment found, it's a new quarter being added
        if (!initialAssignment) {
          return true;
        }

        // Check if any field has changed
        const quotaChanged = assignment.quota !== initialAssignment.quota;
        const nqpGoalChanged = assignment.nqpGoal !== initialAssignment.nqpGoal;
        const rampStatusChanged = assignment.rampStatus !== initialAssignment.rampStatus;

        return quotaChanged || nqpGoalChanged || rampStatusChanged;
      });
    }

    const payload = assignmentsToSend.map(it => ({
      NQP_Goal: it.nqpGoal ? parseInt(it.nqpGoal) : null,
      quota: it.quota ? parseInt(it.quota) : null,
      rampStatus: it.rampStatus,
      fiscalQuarter: it.fiscalQuarter,
      userId: selectedUserId,
      team: team,
      fullName: quotaAddingRepInfo.Full_Name,
      sfdcOwnerId: quotaAddingRepInfo.SFDC_Owner_ID,
    }));

    const createQuotaBody = {
      hireDate,
      team: team,
      quotaAssignments: payload,
    };

    dispatch(createQuotaInfo({ createQuotaBody, showSuccessToast: true }));
    console.log('Dispatching save with:', createQuotaBody);

    setIsSaving(false);
    // handleClose();
  };

  return (
    <>
      <Modal open={open} onClose={handleClose} className={classes.modal}>
        <Box className={classes.modalPaper}>
          <div className={classes.modalHeader}>
            <Typography variant="h6">Add Quota for: {selectedUser}</Typography>
            <IconButton className={classes.closeButton} onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </div>

          <div className={classes.section}>
            <RepInfoSection
              repInfo={quotaAddingRepInfo}
              team={team}
              handleTeamChange={handleTeamChange}
              hireDate={hireDate}
              setHireDate={setHireDate}
              teamHierarchy={teamHierarchy}
            />
            {alert.open && (
              <Alert 
                  severity={alert.severity} 
                  onClose={() => setAlert({ ...alert, open: false })}
                  style={{ marginTop: '1rem' }}
              >
                  {alert.message}
              </Alert>
            )}
          </div>

          <QuotaAssignmentSection
            assignments={quotaAssignments}
            onQuotaChange={handleQuotaChange}
            onRemoveQuota={handleRemoveQuota}
            onAddPreviousQuarter={handleAddPreviousQuarter}
            onAddNextQuarter={handleAddNextQuarter}
            canAddPreviousQuarter={canAddPreviousQuarter}
            canAddNextQuarter={canAddNextQuarter}
            allAvailableQuarters={allAvailableQuartersSorted}
            isLoadingQuarters={!quotaAddingQuarters || isLoadingRepInfo}
            isSaving={isSaving}
            currencySymbol={currencySymbol}
          />

          <div className={classes.actions}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={isSaving || !hasAtLeastOneValidAssignment || !hasChanges}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </Box>
      </Modal>
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
      >
        <DialogTitle id="confirm-dialog-title">Confirm Add Quota</DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-dialog-description">
            Are you sure you want to add quota entries for <span style={{fontWeight:600}}>{selectedUser}</span> for the selected fiscal quarters?
          </DialogContentText>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button onClick={handleConfirmSave} variant="contained" color="primary">
            Yes
          </Button>
          <Button onClick={() => setConfirmOpen(false)} variant="contained" color="primary">
            No
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default QuotaEditorModal;
